<script setup>
import { useForm } from "@/form";
import AppLink from "@Components/AppLink.vue";
import { EyeIcon, PencilLineIcon } from "lucide-vue-next";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { MagicIcon, WordPressIcon } from "@Icons/index";
import Badge from "@Components/Badge.vue";
import { PrimaryButton } from "@Components/Button";

const { t: __ } = useI18n();

const loaded = ref(false);
const singleModeGenerations = ref([]);

onMounted(async () => {
    singleModeGenerations.value = await useForm({ limit: 8 }).post("single_mode_latest");
    loaded.value = true;
});
</script>

<template>
    <div>
        <div class="flex justify-between items-center">
            <div class="space-y-1">
                <h2 class="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">{{ __("Mes contenus récents") }}</h2>
            </div>
        </div>

        <div class="mt-6">
            <div v-if="singleModeGenerations.length > 0" class="grid gap-3 2xl:grid-cols-4 lg:grid-cols-3 sm:grid-cols-2">
                <AppLink
                    :to="{ name: 'single-mode.show', params: { id: generation.id } }"
                    v-for="generation in singleModeGenerations"
                    :class="[
                        'flex flex-col rounded-lg overflow-hidden group',
                        'ring-inset focus:ring-2 focus:ring-inset',
                        'bg-gray-50 ring-gray-300 hover:bg-gray-100 focus:ring-gray-950',
                        'dark:bg-gray-900 dark:ring-gray-800 dark:hover:bg-gray-800 dark:focus:ring-gray-700',
                    ]">
                    <div class="relative flex-none">
                        <img v-if="generation.thumbnail" :src="generation.thumbnail.src" class="w-full h-36 object-cover" />
                        <div v-else class="w-full h-36 bg-gray-200 dark:bg-gray-800"></div>
                        <div
                            :class="[
                                'bg-black/30 group-hover:bg-black/10 dark:bg-black/50 dark:group-hover:bg-black/30 transition-all duration-300 absolute inset-0',
                            ]"></div>
                        <div class="absolute top-2 right-2">
                            <Badge v-if="generation.status === 'idle' || generation.status === 'pending'" color="yellow">
                                {{ __("En cours") }}
                            </Badge>
                            <Badge v-else-if="generation.status === 'failed'" color="red">
                                {{ __("Échoué") }}
                            </Badge>
                            <Badge v-else-if="generation.status === 'success'" color="green">
                                {{ __("Terminé") }}
                            </Badge>
                            <Badge v-else>
                                {{ generation.status }}
                            </Badge>
                        </div>
                    </div>
                    <div class="flex-1">
                        <div class="p-4">
                            <div class="text-sm font-medium line-clamp-2 text-gray-900 dark:text-white">{{ generation.title }}</div>
                        </div>
                        <div v-if="generation.post_id" class="grid grid-cols-3">
                            <AppLink
                                @click.stop
                                :to="{ name: 'single-mode.show', params: { id: generation.id } }"
                                class="flex justify-center items-center gap-2 p-2 dark:hover:bg-gray-700 hover:bg-gray-200">
                                <MagicIcon class="w-4 h-4" />
                                {{ __("Modifier") }}
                            </AppLink>
                            <a
                                @click.stop
                                :href="generation.edit_link"
                                target="_blank"
                                class="flex justify-center items-center gap-2 p-2 dark:hover:bg-gray-700 hover:bg-gray-200">
                                <WordPressIcon class="w-4 h-4" />
                                {{ __("Ouvrir") }}
                            </a>
                            <a
                                @click.stop
                                :href="generation.display_link"
                                target="_blank"
                                class="flex justify-center items-center gap-2 p-2 dark:hover:bg-gray-700 hover:bg-gray-200">
                                <EyeIcon class="w-4 h-4" />
                                {{ __("Voir") }}
                            </a>
                        </div>
                    </div>
                </AppLink>
            </div>
            <AppLink
                v-else-if="loaded"
                :to="{ name: 'single-mode.create' }"
                type="button"
                class="w-full relative block p-8 text-center rounded-lg border-2 border-gray-300 border-dashed group dark:border-gray-700 dark:hover:border-white focus:border-gray-900 focus:outline-none hover:border-gray-900">
                <PencilLineIcon class="mx-auto w-10 h-10 text-gray-600 dark:text-gray-400" />
                <div class="mt-6 text-lg font-semibold text-gray-900 dark:text-white">{{ __("Aucun contenu récent") }}</div>
                <div class="mt-3 text-sm text-gray-600 dark:text-gray-400">{{ __("Vous n'avez pas encore créé de contenu.") }}</div>
                <div class="flex justify-center mt-6">
                    <PrimaryButton :icon="MagicIcon">
                        {{ __("Générer du contenu IA") }}
                    </PrimaryButton>
                </div>
            </AppLink>
        </div>
        <div v-if="singleModeGenerations.length >= 8" class="flex justify-end mt-3">
            <AppLink
                :to="{ name: 'single-mode.create' }"
                class="text-sm text-gray-500 dark:text-gray-400 dark:hover:text-gray-300 hover:text-gray-700 hover:underline">
                {{ __("Voir tous les contenus") }}
            </AppLink>
        </div>
    </div>
</template>
